import { useState } from 'react'
import { useService } from '../redux/reducer/service'
import { useContent } from '../redux/reducer/content'
import {
  tasksForSavingProductsToGA,
  toBasketQuest,
  toBasketRoutineQuest,
  toFavoriteQuest,
  toFavoriteRoutineQuest,
} from '../constants/task'
import { SIMPLE } from '../constants/tasksTypes'
import CustomBridge from '../controllers/bridge/CustomBridge'
import useServiceData from './useServiceData'
import { useRequestsHandlerData } from './useRequestsHandlerData'
import { useOpen } from './useOpen'
import { isArray, isNumber, isString } from 'lodash'
import { useApplicationCallbacks } from '../providers/application-callbacks'
import { gtmFunctions } from '../constants/analytics/GTM'
import { getProductCurrentState } from '../components/features/product/utils/getProductCurrentState'
import { isPlatformBourjoisAssets } from '../utils/game/isPlatformBourjoisAssets'
import { isJumperAdidasAssets } from '../utils/game/isJumperAdidasAssets'

export const useSendProductToGoldenAppleStore = ({
  id,
  sku,
  link,
  group,
  beauty_routine_id,
  hasFavourite,
  filter = null,
}) => {
  const { quests } = useContent()
  const { serviceData } = useService()
  const [isHasFavourite, setIsHasFavourite] = useState(hasFavourite)

  const modalsData = serviceData.modalsData ?? {}
  const productsIds = modalsData.productsIds ?? []
  const hasBasket = productsIds?.includes?.(id)

  const callbacks = useApplicationCallbacks()
  const openLink = useOpen()

  const { state } = useContent()
  const {
    isGameState,
    gameName,
    isBeautyRoutineState,
    isCustomizationState,
    customizationFilterName,
  } = getProductCurrentState(state, filter)

  const helpers = {
    getIsTruthyCondition(quest) {
      const allKeysData = [
        {
          condition: isNumber(beauty_routine_id) && quest?.type === SIMPLE,
          key: toBasketRoutineQuest,
        },
        {
          condition: isNumber(beauty_routine_id) && quest?.type === SIMPLE,
          key: toFavoriteRoutineQuest,
        },
        { condition: true, key: toBasketQuest },
        { condition: true, key: toFavoriteQuest },
      ]

      const filteredKeys = allKeysData.reduce((acc, { condition, key }) => {
        if (condition) acc.push(key)
        return acc
      }, [])

      return filteredKeys.includes(quest.uid)
    },
    getQuestKey(quest) {
      return tasksForSavingProductsToGA[quest.uid]
    },
  }

  const { favoriteQuest, basketQuest } = quests?.reduce?.(
    (acc, quest) => {
      if (helpers.getIsTruthyCondition(quest)) {
        const necessaryArray = acc[helpers.getQuestKey(quest)]
        necessaryArray.push(quest)
      }

      const { subQuests } = quest

      if (isArray(subQuests))
        subQuests.forEach((quest) => {
          if (helpers.getIsTruthyCondition(quest)) {
            const necessaryArray = acc[helpers.getQuestKey(quest)]
            necessaryArray.push(quest)
          }
        })

      return acc
    },
    { favoriteQuest: [], basketQuest: [] },
  )

  const completeSomeQuests = (array) =>
    array.forEach((quest) => {
      if (!quest.isComplete && isString(group)) callbacks.completeQuest({ id: quest.id })
    })

  const { save } = useServiceData()

  const favoriteCallback = (e) => {
    e.stopPropagation()

    if (isHasFavourite) {
      CustomBridge.instance.send({ name: 'REMOVE_FROM_FAVORITES', type: 0, data: { sku } })
      return
    }

    if (isGameState) {
      gtmFunctions.GTMGamesAddToWishlist({ name: gameName, item_sku: sku })

      if (gameName === 'Бьютипад' && isPlatformBourjoisAssets()) {
        gtmFunctions.GTMGamesAddToWishlistBeautyPad({ item_sku: sku })
      }

      if (gameName === 'Прыжки' && isJumperAdidasAssets()) {
        gtmFunctions.GTMGamesJumpToNextProduct({ item_sku: sku })
      }
    }

    if (isBeautyRoutineState) {
      gtmFunctions.GTMBeautyRoutineAddToWishlist({ item_sku: sku })
    }

    if (isCustomizationState) {
      gtmFunctions.GTMMoreWishlist({ block_name: customizationFilterName })
    }

    CustomBridge.instance.send({ name: 'ADD_TO_FAVORITES', type: 0, data: { sku } })

    completeSomeQuests(favoriteQuest)
  }

  const basketCallback = (e) => {
    e.stopPropagation()

    if (hasBasket) {
      save('modalsData', { ...modalsData, productsIds: productsIds.filter((i) => i !== id) })
      return
    }

    if (isGameState) {
      gtmFunctions.GTMGamesAddToCart({ name: gameName, item_sku: sku })

      if (gameName === 'Бьютипад' && isPlatformBourjoisAssets()) {
        gtmFunctions.GTMGamesAddToCartBeautyPad({ item_sku: sku })
      }

      if (gameName === 'Прыжки' && isJumperAdidasAssets()) {
        gtmFunctions.GTMGamesJumpToPrevProduct({ item_sku: sku })
      }
    }

    if (isBeautyRoutineState) {
      gtmFunctions.GTMBeautyRoutineAddToCart({ item_sku: sku })
    }

    if (isCustomizationState) {
      gtmFunctions.GTMMoreBuyForSelf({ block_name: customizationFilterName })
    }

    save('modalsData', { ...modalsData, productsIds: [...productsIds, id] })

    CustomBridge.instance.send({ name: 'ADD_TO_CART', type: 0, data: { sku } })

    callbacks.productToCart(sku)

    completeSomeQuests(basketQuest)
  }

  const openCallback = (e) => {
    e.stopPropagation()

    if (link) {
      openLink(link)

      if (isGameState) {
        gtmFunctions.GTMGamesProduct({ name: gameName, item_sku: sku })

        if (gameName === 'Бьютипад' && isPlatformBourjoisAssets()) {
          gtmFunctions.GTMGamesClickProductBeautyPad({ item_sku: sku })
        }

        if (gameName === 'Прыжки' && isJumperAdidasAssets()) {
          gtmFunctions.GTMGamesJumpToProduct({ item_sku: sku })
        }
      }

      if (isBeautyRoutineState) {
        gtmFunctions.GTMBeautyRoutineProduct({ item_sku: sku })
      }
    }
  }

  const isPending = useRequestsHandlerData([
    {
      address: 'requestsProductToFavorites',
      fulfilled({ product: { id: productId, hasFavourite } }) {
        productId === id && setIsHasFavourite(hasFavourite)
      },
    },
    { address: 'requestsUserServiceDataSave' },
    { address: 'requestsGameCompleteQuest' },
  ])

  return {
    favoriteCallback,
    basketCallback,
    openCallback,
    hasBasket,
    hasFavourite: isHasFavourite,
    isPending,
  }
}
